import { useCallback, useEffect, useState } from "react";
import { getDuplicateCount } from "../services/prospects";

/**
 * The open duplicate-group count behind the nav badge.
 *
 * Two components need this number and they must agree: the badge in
 * `DashboardNav` and the Merge Manager itself, which drops the count after a
 * dismissal or merge. A plain per-component hook would fetch twice and leave the
 * badge stale after a resolution, so the value lives in a tiny module-level store
 * that both subscribe to — the same shape as a context provider, without
 * threading one through the dashboard tree for a single integer.
 *
 * The count is always read from the server's stored rows; it never triggers an
 * org-wide scan, so refreshing it on resolution is cheap.
 */

let cachedCount = 0;
let inFlight: Promise<void> | null = null;
const listeners = new Set<(count: number) => void>();

const publish = (count: number) => {
  cachedCount = count;
  listeners.forEach((listener) => listener(count));
};

const fetchCount = (): Promise<void> => {
  // Collapse concurrent refreshes (mount + a resolution landing together) into one request.
  if (inFlight) return inFlight;

  inFlight = getDuplicateCount()
    .then(publish)
    .catch(() => {
      // A failed badge fetch is not worth surfacing — the badge simply keeps its
      // last known value rather than pushing an error into the nav chrome.
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/** Test-only reset so the module store does not leak between test cases. */
export const __resetDuplicateCountStore = () => {
  cachedCount = 0;
  inFlight = null;
  listeners.clear();
};

export const useDuplicateCount = () => {
  const [count, setCount] = useState(cachedCount);

  useEffect(() => {
    listeners.add(setCount);
    void fetchCount();
    return () => {
      listeners.delete(setCount);
    };
  }, []);

  const refresh = useCallback(() => {
    void fetchCount();
  }, []);

  return { count, refresh };
};
